// Shared loading / empty / error renderers. Keeps every screen behaving
// consistently (feed, title, shorts, search, library). 04-web polish.
import { h, mount } from "./dom.js";
import { t } from "./i18n.js";
import { ApiError } from "./api.js";

/** Human-readable message from an unknown thrown value. */
export function errorMessage(e: unknown): string {
  if (e instanceof ApiError) return `${e.status} ${e.code}`;
  return String(e);
}

/** Indeterminate spinner with an accessible status role. */
export function spinner(message: string = t("common.loading")): HTMLElement {
  return h(
    "div",
    { class: "state state-loading", role: "status", "aria-live": "polite" },
    h("span", { class: "spinner", "aria-hidden": "true" }),
    h("span", { class: "muted" }, message),
  );
}

/** A grid of poster-shaped skeletons (matches the feed card layout). */
export function skeletonGrid(count = 12): HTMLElement {
  return h(
    "div",
    { class: "grid", "aria-hidden": "true" },
    ...Array.from({ length: count }, () =>
      h(
        "div",
        { class: "card skeleton-card" },
        h("div", { class: "poster skeleton" }),
        h(
          "div",
          { class: "meta" },
          h("div", { class: "skeleton skeleton-line" }),
          h("div", { class: "skeleton skeleton-line short" }),
        ),
      ),
    ),
  );
}

/** Neutral empty state. */
export function emptyState(message: string): HTMLElement {
  return h("div", { class: "state center muted", role: "status" }, message);
}

/** Error state with an optional retry button. */
export function errorState(message: string, onRetry?: () => void): HTMLElement {
  return h(
    "div",
    { class: "state center", role: "alert" },
    h("p", { class: "error" }, message),
    onRetry
      ? h("button", { type: "button", class: "btn secondary", onclick: onRetry }, t("common.retry"))
      : null,
  );
}

interface LoadOptions<T> {
  root: HTMLElement;
  load: () => Promise<T>;
  render: (data: T) => Node;
  /** Treat the result as empty (renders the empty state instead). */
  isEmpty?: (data: T) => boolean;
  empty?: string;
  /** Custom loading node (e.g. skeletonGrid()). Defaults to a spinner. */
  loading?: () => Node;
}

/**
 * Standard async screen flow: loading → (empty | content | error+retry).
 * The retry button re-runs the same flow into the same root.
 */
export async function loadInto<T>(opts: LoadOptions<T>): Promise<void> {
  const { root, load, render, isEmpty, empty, loading } = opts;
  mount(root, loading ? loading() : spinner());
  try {
    const data = await load();
    if (isEmpty?.(data)) {
      mount(root, emptyState(empty ?? ""));
      return;
    }
    mount(root, render(data));
  } catch (e) {
    mount(root, errorState(t("common.loadFailed", { msg: errorMessage(e) }), () => void loadInto(opts)));
  }
}
